/*
 *  -----------------------------------------------------------------------
 *    <copyright file="add-visit-schedule.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';
import cn from 'clsx';
import moment from 'moment';
import {
    AddVisitScheduleFormInterface,
    AddVisitScheduleInterface,
} from '@components/admin/add-visit-schedule/add-visit-schedule.interface';
import { SVTScheduleVersionFields } from '@components/admin/schedule-versions-grid/schedule-versions-grid.interface';
import WarningForDialog from '@components/admin/warning-for-dialog/warning-for-dialog';
import CopyButton from '@components/core/copy-button';
import DatePicker from '@components/core/date-picker';
import Loading from '@components/core/loading';
import ModalUpdatedInfo from '@components/core/modal-updated-info';
import MultiSelectLov from '@components/core/multi-select/multi-select-lov';
import { useTranslation } from 'hooks';
import {
    GET_STUDY_VISIT_SCHEDULES,
    SAVE_STUDY_VISIT_SCHEDULE,
    SAVE_STUDY_VISIT_SCHEDULE_VERSION,
    SAVE_STUDY_VISIT_SCHEDULE_VISITS,
} from 'queries/study-visit-schedules';
import { StudySubjectVisitScheduleTemplateInput } from 'types/graphql-global-types';
import { checkConfigIdErrors, checkIfNameExist, validateDate, validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import TextField from 'views/components/core/text-field';
import Constants from '@utils/constants';
import { formatDateUTC_DEPRECATED } from '@utils/convertor';
import {
    StudyTreatmentArmFields,
    StudyVisitScheduleFields,
    StudyCohortFields,
} from '@queries/generated/fragments.generated';
import {
    saveStudyVisitSchedule,
    saveStudyVisitScheduleVariables,
    saveStudyVisitScheduleVersion,
    saveStudyVisitScheduleVersionVariables,
    saveStudyVisitScheduleVisits,
    saveStudyVisitScheduleVisitsVariables,
} from '@queries/generated/study-visit-schedules.generated';

import './styles.scss';

const initialFormValues = {
    description: '',
    name: '',
    version: '',
    versionDate: null,
    active: false,
    treatmentArmIds: [],
    cohortIds: [],
    configId: null,
};

const AddVisitSchedule = ({
    currentSchedule,
    schedules,
    allSchedules,
    onClose,
    studyId,
    scheduleDialogType,
    treatmentArms,
    cohorts,
    isSysAdmin,
}: AddVisitScheduleInterface) => {
    const { t } = useTranslation();

    const {
        control,
        handleSubmit,
        getValues,
        formState: { errors },
        reset,
    } = useForm<AddVisitScheduleFormInterface>({
        mode: 'all',
        defaultValues: initialFormValues,
    });

    const [saveVisitSchedule, { loading: saveVisitScheduleLoading }] = useMutation<
        saveStudyVisitSchedule,
        saveStudyVisitScheduleVariables
    >(SAVE_STUDY_VISIT_SCHEDULE);

    const [saveVisitScheduleVersion, { loading: saveVisitScheduleVersionLoading }] = useMutation<
        saveStudyVisitScheduleVersion,
        saveStudyVisitScheduleVersionVariables
    >(SAVE_STUDY_VISIT_SCHEDULE_VERSION);

    const [saveVisits, { loading: saveVisitsLoading }] = useMutation<
        saveStudyVisitScheduleVisits,
        saveStudyVisitScheduleVisitsVariables
    >(SAVE_STUDY_VISIT_SCHEDULE_VISITS);

    const isAddGroup = scheduleDialogType === Constants.scheduleDialogTypes.addScheduleGroup;
    const isAddVersion = scheduleDialogType === Constants.scheduleDialogTypes.addScheduleVersion;
    const isEditGroup = scheduleDialogType === Constants.scheduleDialogTypes.editScheduleGroup;
    const isEditVersion = scheduleDialogType === Constants.scheduleDialogTypes.editScheduleVersion;
    const [latestDate, setLatestDate] = useState<Date | null>(null);

    useEffect(() => {
        if (schedules?.length && currentSchedule) {
            const lastIndex = isEditVersion ? schedules.length - 2 : schedules.length - 1;
            const definedLastDate = new Date(schedules[lastIndex]?._effectiveDate || schedules[lastIndex]?.created);

            definedLastDate.setDate(definedLastDate.getDate() + 1);

            setLatestDate(definedLastDate);
        }
    }, [schedules, currentSchedule, isEditVersion]);

    const currentScheduleVersionCount = schedules?.filter(
        (item: SVTScheduleVersionFields) => item.groupId === currentSchedule?.groupId,
    ).length;

    useEffect(() => {
        if (currentSchedule && (isEditGroup || isEditVersion || isAddVersion)) {
            reset({
                id: !isAddVersion ? currentSchedule.id : null,
                description: !isAddVersion ? currentSchedule.description : '',
                name: currentSchedule.name,
                version: !isAddVersion ? currentSchedule.version : '',
                versionDate: !isAddVersion ? formatDateUTC_DEPRECATED(currentSchedule?.versionDate?.time, true) : null,
                active: currentSchedule.active,
                treatmentArmIds: currentSchedule?.treatmentArmIds,
                cohortIds: currentSchedule?.cohortIds,
                configId: isEditGroup || isEditVersion ? currentSchedule?.configId : null,
            });
        }
    }, [currentSchedule, scheduleDialogType]);

    const validate = (value: string, field: string, length?: number) => {
        const error = validateFields(value, field, length);

        return error[field];
    };

    const validateName = (value: string) => {
        const allState = getValues();
        const isNameExist = checkIfNameExist({ ...allState, name: value.trim(), id: null }, schedules, 'name');
        const checkCurrentName = currentSchedule && currentSchedule.name !== value && isNameExist;

        if ((isNameExist && currentSchedule === null) || checkCurrentName) return t('admin:scheduleNameAlreadyExists');
    };

    const validateVersion = (value: string) => {
        const groupInFocuse = schedules.filter(
            (schedule: StudyVisitScheduleFields) => schedule.groupId === currentSchedule?.groupId,
        );
        const allState = getValues();
        const isVersionExist = checkIfNameExist(
            { ...allState, version: value.trim(), id: null },
            groupInFocuse,
            'version',
        );
        const checkCurrentVersion = currentSchedule?.version !== value && isVersionExist;
        const isValidVersion = isAddVersion ? isVersionExist : checkCurrentVersion;

        if (isValidVersion) return t('admin:versionShouldBeUnique');
    };

    const getFilteredMultiselectOptions = (fieldName: 'treatmentArmIds' | 'cohortIds') => {
        const selectedItemIds = getValues(fieldName) ?? [];

        if (fieldName === 'treatmentArmIds') {
            return treatmentArms?.filter((option: StudyTreatmentArmFields) => !selectedItemIds.includes(option.id));
        }

        return cohorts?.filter((option: StudyCohortFields) => !selectedItemIds.includes(option.id));
    };

    const isEffectiveDateDisabled = () => {
        if (isEditVersion && schedules?.length >= 1) {
            const lastVersion = schedules[schedules.length - 1];

            return currentSchedule?.id !== lastVersion.id;
        }

        return false;
    };

    const isValidationEffectiveDateNeed = () => {
        const isSingleVersionInGroup = schedules?.length === 1 && schedules[0].id === currentSchedule?.id;

        return (
            ((isAddVersion || isEditVersion) && !isEffectiveDateDisabled() && !isSingleVersionInGroup) ||
            (isAddVersion && isSingleVersionInGroup)
        );
    };

    const versionsDateValidate = (value: Date) => {
        if (isValidationEffectiveDateNeed()) {
            const currentDate = new Date(formatDateUTC_DEPRECATED(value, true));

            if (latestDate && currentDate.getTime() < latestDate.getTime()) {
                return t('admin:versionEffectiveDateError', {
                    versionDateLatest: formatDateUTC_DEPRECATED(latestDate, false, 'DD-MMM-YYYY'),
                });
            }
        }
    };

    const outputCohortsTANames = (fieldName: 'treatmentArmIds' | 'cohortIds') => {
        const arrayId = getValues(fieldName) ?? [];
        const sourceArray = fieldName === 'treatmentArmIds' ? treatmentArms : cohorts;
        const resultArray =
            sourceArray?.filter((option: StudyTreatmentArmFields | StudyCohortFields) => arrayId.includes(option.id)) ??
            [];
        const namesArray = resultArray.map((o: StudyTreatmentArmFields | StudyCohortFields) => o.name);

        return resultArray.length ? namesArray.join(', ') : t('ra:n/a');
    };

    const saveSchedule = (submitData: StudySubjectVisitScheduleTemplateInput) => {
        saveVisitSchedule({
            variables: {
                studyId,
                visitSchedule: submitData,
                reason: '',
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then(() => {
                onClose(isEditGroup ? 'edit' : 'add');
            })
            .catch(() => {
                onClose('error');
            });
    };

    const saveScheduleVersion = (submitData: StudySubjectVisitScheduleTemplateInput) => {
        saveVisitScheduleVersion({
            variables: {
                studyId,
                visitScheduleVersion: submitData,
                reason: '',
            },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then((result: any) => {
                if (result?.data?.saveStudyVisitScheduleVersion === null) {
                    onClose('error');
                } else {
                    onClose(isEditVersion ? 'editVersion' : 'addVersion');
                }
            })
            .catch(() => {
                onClose('error');
            });
    };

    const onSubmit = (submitData: any) => {
        const scheduleData = {
            id: isEditGroup || isEditVersion ? currentSchedule?.id : null,
            groupId: !isAddGroup ? currentSchedule?.groupId : null,
            description: submitData.description,
            name: submitData.name,
            version: submitData.version,
            versionDate: {
                ignore: 1,
                time: formatDateUTC_DEPRECATED(submitData.versionDate, false, 'YYYY-MM-DD[T]HH:mm:ss[Z]'),
            },
            active: submitData.active,
            treatmentArmIds: submitData.treatmentArmIds,
            cohortIds: submitData.cohortIds,
            configId: submitData.configId,
        };

        if (isAddGroup || isEditGroup) {
            saveSchedule(scheduleData);
        } else if (isAddVersion || isEditVersion) {
            saveScheduleVersion(scheduleData);
        }
    };

    if (saveVisitScheduleLoading || saveVisitsLoading || saveVisitScheduleVersionLoading) {
        return <Loading float />;
    }

    return (
        <CustomDialog fullWidth maxWidth="md" open>
            <form id="visit-schedule-form" className="schedule-form" onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle id="add-critical-variable-dialog">
                    {(isAddGroup || isEditGroup) &&
                        t(currentSchedule ? 'admin:editScheduleDetails' : 'admin:addSchedule')}

                    {(isAddVersion || isEditVersion) &&
                        (isEditVersion
                            ? t('admin:editVersionID', { versionId: currentSchedule?.recordId })
                            : t('admin:addVersion'))}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={() => {
                            onClose(false);
                        }}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent style={{ padding: 20, paddingTop: 20, paddingBottom: 0 }}>
                    {isEditGroup && (
                        <WarningForDialog
                            customText={t('admin:editScheduleVersionsWarning', {
                                versionsCount: currentScheduleVersionCount,
                            })}
                        />
                    )}

                    {(isAddVersion || isEditVersion) && (
                        <>
                            <div className="mb-10">
                                {currentSchedule?.name && (
                                    <span>
                                        <strong>{t('admin:scheduleName')}: </strong>
                                        {currentSchedule?.name}
                                    </span>
                                )}
                            </div>
                            <div className="info-c-ta mb-20">
                                <div>
                                    <span>
                                        <strong>{t('admin:cohortsGridTitle')}: </strong>
                                        {outputCohortsTANames('cohortIds')}
                                    </span>
                                </div>
                                <div>
                                    <span>
                                        <strong>{t('admin:treatmentArmsGridTitle')}: </strong>
                                        {outputCohortsTANames('treatmentArmIds')}
                                    </span>
                                </div>
                            </div>
                        </>
                    )}
                    {(isAddGroup || isEditGroup) && (
                        <Controller
                            control={control}
                            name="active"
                            render={({ field: { onChange, value } }) => (
                                <div className="checkboxes-wrapper">
                                    <div className="left-part mb-15">
                                        <Checkbox
                                            rounded="none"
                                            id="schedule-active-checkbox"
                                            className="cell-checkbox no-border mr"
                                            checked={value}
                                            onChange={onChange}
                                            label={t('common:active')}
                                            disabled={isSysAdmin}
                                            data-testid="schedule-active-checkbox"
                                            size="medium"
                                        />
                                    </div>
                                </div>
                            )}
                        />
                    )}
                    <div className={cn({ 'version-date-wrapp': isAddVersion || isEditVersion })}>
                        <div className="type-edc-wrapper time-picker">
                            {(isAddGroup || isEditGroup) && (
                                <Controller
                                    control={control}
                                    name="name"
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('ra:requiredFieldError'),
                                        },
                                        validate: {
                                            maxLength: (value: string) => validate(value, 'name', 60),
                                            existName: (value: string) => validateName(value),
                                        },
                                    }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <div className={cn('form-control-type', currentSchedule ? 'not-full' : 'mr')}>
                                            <TextField
                                                label={`${t('name')}*`}
                                                id="add-schedule-name"
                                                fullWidth
                                                value={value}
                                                onChange={onChange}
                                                placeholder={t('admin:enterName')}
                                                helperText={
                                                    <CharactersRemaining
                                                        id="create-schedule-dialog-name"
                                                        length={value?.length}
                                                        max={60}
                                                        error={error?.message}
                                                    />
                                                }
                                                variant="outlined"
                                                color="secondary"
                                                error={!!error?.message}
                                                disabled={isSysAdmin}
                                            />
                                        </div>
                                    )}
                                />
                            )}
                            {!isEditGroup && (
                                <Controller
                                    control={control}
                                    name="versionDate"
                                    rules={{
                                        required: {
                                            value: true,
                                            message: t('ra:requiredFieldError'),
                                        },
                                        validate: {
                                            validDate: (value: any) => {
                                                if (!validateDate(value)) {
                                                    return t('dateInvalid');
                                                }
                                            },
                                            versionsDate: (value: any) => versionsDateValidate(value),
                                        },
                                    }}
                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                        <div className="form-action-type">
                                            <DatePicker
                                                label={`${t('admin:effectiveDate')}*`}
                                                error={error?.message}
                                                customId="schedule-version-date"
                                                // labelWidth={145}
                                                disabled={isEffectiveDateDisabled() || isSysAdmin}
                                                selectedDate={value}
                                                handleDateChange={(v: any) =>
                                                    onChange(v?.value ? moment(v?.value) : null)
                                                }
                                                min={isValidationEffectiveDateNeed() ? latestDate : undefined}
                                            />
                                        </div>
                                    )}
                                />
                            )}
                        </div>

                        {!isEditGroup && (
                            <div className="type-edc-wrapper mb-0">
                                <div
                                    className={cn('form-control-type', {
                                        'not-full': isAddGroup || isEditGroup,
                                        mr: isAddVersion || isEditVersion,
                                    })}
                                >
                                    <Controller
                                        control={control}
                                        name="version"
                                        rules={{
                                            required: {
                                                value: true,
                                                message: t('ra:requiredFieldError'),
                                            },
                                            validate: {
                                                maxLength: (value: string) => validate(value, 'version', 30),
                                                existVersion: (value: string) => validateVersion(value),
                                            },
                                        }}
                                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                                            <TextField
                                                label={`${t('version')}*`}
                                                id="add-schedule-version"
                                                fullWidth
                                                value={value}
                                                onChange={onChange}
                                                placeholder={t('admin:enterVersion')}
                                                helperText={
                                                    <CharactersRemaining
                                                        id="create-schedule-dialog-version"
                                                        length={value?.length || 0}
                                                        max={30}
                                                        error={error?.message}
                                                    />
                                                }
                                                variant="outlined"
                                                color="secondary"
                                                error={!!error?.message}
                                                disabled={isSysAdmin}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {(isAddGroup || isEditGroup) && (
                        <Controller
                            control={control}
                            name="treatmentArmIds"
                            render={({ field: { onChange, value } }) => (
                                <div className="type-edc-wrapper mb-35">
                                    <MultiSelectLov
                                        items={getFilteredMultiselectOptions('treatmentArmIds') || []}
                                        onChange={(selectedObject: StudyTreatmentArmFields[]) => {
                                            const selectedTreatmentArmIds = selectedObject.map(
                                                (ta: StudyTreatmentArmFields) => ta.id,
                                            );
                                            onChange(selectedTreatmentArmIds);
                                        }}
                                        value={treatmentArms?.filter((option: StudyTreatmentArmFields) =>
                                            value?.includes(option.id),
                                        )}
                                        label={t('admin:treatmentArmsGridTitle')}
                                        customId="treatment-arms-ids"
                                        customClassName="form-action-type"
                                        placeholder={t('enterSearchTerm')}
                                        field={t('admin:treatmentArmsGridTitle')}
                                        disabled={isSysAdmin}
                                    />
                                </div>
                            )}
                        />
                    )}
                    {(isAddGroup || isEditGroup) && (
                        <Controller
                            control={control}
                            name="cohortIds"
                            render={({ field: { onChange, value } }) => (
                                <div className="type-edc-wrapper mb-35">
                                    <MultiSelectLov
                                        items={getFilteredMultiselectOptions('cohortIds') || []}
                                        onChange={(selectedObject: StudyCohortFields[]) => {
                                            const selectedCohortsIds = selectedObject.map(
                                                (c: StudyCohortFields) => c.id,
                                            );
                                            onChange(selectedCohortsIds);
                                        }}
                                        value={
                                            cohorts?.filter((option: StudyCohortFields) =>
                                                value?.includes(option.id),
                                            ) ?? []
                                        }
                                        label={t('admin:cohortsGridTitle')}
                                        field={t('admin:cohortsGridTitle')}
                                        customId="cohortIds-ids"
                                        customClassName="form-action-type"
                                        placeholder={t('enterSearchTerm')}
                                        disabled={isSysAdmin}
                                    />
                                </div>
                            )}
                        />
                    )}
                    {!isEditGroup && (
                        <Controller
                            control={control}
                            name="description"
                            rules={{
                                validate: {
                                    maxLength: (value: string) => validate(value, 'description', 600),
                                },
                            }}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <TextField
                                    label={`${t('description')}`}
                                    id="add-schedule-description"
                                    multiline
                                    rows={5}
                                    fullWidth
                                    value={value}
                                    onChange={onChange}
                                    placeholder={t('ra:criticalVariableDescriptionHelperText')}
                                    helperText={
                                        <CharactersRemaining
                                            id="create-cohort-dialog-description"
                                            length={value?.length}
                                            max={600}
                                            error={error?.message}
                                        />
                                    }
                                    variant="outlined"
                                    color="secondary"
                                    error={!!error?.message}
                                    className={cn('before-select', { 'mb-10': isEditVersion, mb: !isEditVersion })}
                                    disabled={isSysAdmin}
                                />
                            )}
                        />
                    )}
                    {isEditVersion && (
                        <div className="type-edc-wrapper mb-20">
                            <Controller
                                control={control}
                                name="configId"
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('ra:requiredFieldError'),
                                    },
                                    validate: (value) => checkConfigIdErrors(getValues(), allSchedules, value),
                                }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <TextField
                                        id="edit-field-config-id"
                                        label={t('admin:configId')}
                                        onChange={onChange}
                                        fullWidth
                                        value={value}
                                        size="small"
                                        slotProps={{
                                            input: {
                                                readOnly: !isSysAdmin,
                                                endAdornment: <CopyButton value={value ?? ''} />,
                                            },
                                        }}
                                        helperText={
                                            <span style={{ color: '#ae0015' }} className="remaining-text">
                                                {error?.message}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!error?.message}
                                    />
                                )}
                            />
                        </div>
                    )}
                </DialogContent>
                <DialogActions
                    className={cn('actions-info-wrapper-edit-cv schedule-dialog-actions', {
                        'flex-end': !isEditVersion,
                    })}
                >
                    {isEditVersion && <ModalUpdatedInfo item={currentSchedule} />}
                    <Button
                        disabled={Object.keys(errors).length !== 0}
                        text={isEditGroup || isEditVersion ? 'update' : 'add'}
                        id="edit-schedule-save-close"
                        type="submit"
                    />
                </DialogActions>
            </form>
        </CustomDialog>
    );
};

export default AddVisitSchedule;
